import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { listingPhotoAlt } from '../../../util/listingSeo';
import { ResponsiveImage } from '../../../components';

import css from './SectionPhotoMosaic.module.css';

const GridIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const ShareIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 3v12M12 3l-4 4M12 3l4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const variantSet = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];

// The first photo is the LCP element (mobile: first carousel slide, desktop: hero tile). Both
// get the same `sizes`, so at any viewport they pick the same srcset candidate and the browser
// downloads it once, at high priority. Every other photo is lazy: on mobile the remaining slides
// sit off-screen to the right, and the desktop tiles are display:none — without `loading="lazy"`
// all of them downloaded in parallel with the LCP photo and pushed it back by seconds.
const HERO_SIZES = '(min-width: 1024px) 60vw, 100vw';
const heroImageProps = { fetchpriority: 'high', loading: 'eager' };
const deferredImageProps = { loading: 'lazy', decoding: 'async' };

const MosaicTile = ({ image, alt, variants, className, onClick, sizes, ariaLabel, imageProps }) => (
  <button
    type="button"
    className={classNames(css.tile, className)}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    <ResponsiveImage
      rootClassName={css.tileImage}
      image={image}
      alt={alt}
      variants={variants}
      sizes={sizes}
      {...imageProps}
    />
  </button>
);

/**
 * SectionPhotoMosaic
 *
 * Airbnb-style photo grid (desktop) / swipe carousel (mobile).
 */
const SectionPhotoMosaic = props => {
  const { listing, variantPrefix, onRequestFullscreen, imageAltTitle, intl: intlProp } = props;
  const intl = intlProp || useIntl();
  const images = listing?.images || [];

  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);
  useEffect(() => () => copyTimerRef.current && clearTimeout(copyTimerRef.current), []);

  // Mobile carousel state — hooks must be called unconditionally
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      if (w > 0) {
        const idx = Math.round(el.scrollLeft / w);
        setActiveIdx(Math.min(Math.max(idx, 0), Math.max(images.length - 1, 0)));
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [images.length]);

  if (!images.length) return null;

  const variants = variantPrefix
    ? [variantPrefix, `${variantPrefix}-2x`, `${variantPrefix}-4x`, ...variantSet]
    : variantSet;

  // "<listing> – Foto 2 von 6" instead of the hosted "Eintragsfoto 2/6" (~1,700 photos without
  // a descriptive alt). The listing title is the fallback when no SEO title is passed in.
  const altTitle = imageAltTitle || listing?.attributes?.title;
  const altFor = i => listingPhotoAlt(intl, { title: altTitle, index: i, count: images.length });

  const openAt = i => {
    if (typeof onRequestFullscreen === 'function') onRequestFullscreen(i);
  };

  const handleShare = async () => {
    if (typeof window === 'undefined') return;
    const shareData = {
      title: typeof document !== 'undefined' ? document.title : '',
      url: window.location.href,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        /* user cancelled */
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        /* clipboard blocked */
      }
    }
  };

  const showAllLabel = <FormattedMessage id="SectionPhotoMosaic.showAllPhotos" />;

  // Desktop layout — choose by available image count
  const count = images.length;
  let desktopLayoutClass = css.gridFive;
  if (count === 1) desktopLayoutClass = css.gridOne;
  else if (count === 2) desktopLayoutClass = css.gridTwo;
  else if (count === 3) desktopLayoutClass = css.gridThree;
  else if (count === 4) desktopLayoutClass = css.gridFour;

  const desktopTiles = images
    .slice(0, 5)
    .map((img, i) => (
      <MosaicTile
        key={img.id?.uuid || i}
        image={img}
        alt={altFor(i)}
        variants={variants}
        className={classNames(css[`tile${i}`], { [css.heroTile]: i === 0 })}
        sizes={i === 0 ? HERO_SIZES : '(min-width: 1024px) 20vw, 100vw'}
        imageProps={i === 0 ? heroImageProps : deferredImageProps}
        onClick={() => openAt(i)}
        ariaLabel={intl.formatMessage(
          { id: 'SectionPhotoMosaic.openPhotoInGallery' },
          { index: i + 1 }
        )}
      />
    ));

  const showAllAriaLabel = intl.formatMessage(
    { id: 'SectionPhotoMosaic.showAllPhotosWithCount' },
    { count: images.length }
  );

  return (
    <section className={css.root}>
      {/* Desktop grid */}
      <div className={classNames(css.desktop, desktopLayoutClass)}>
        {desktopTiles}

        <div className={css.shareWrapper}>
          <button type="button" className={css.shareButton} onClick={handleShare}>
            <ShareIcon />
            <span className={css.shareLabel}>
              <FormattedMessage id="ListingPage.share" />
            </span>
          </button>
          {copied ? (
            <span className={css.copiedToast} role="status" aria-live="polite">
              <FormattedMessage id="SectionPhotoMosaic.linkCopied" defaultMessage="Link copied" />
            </span>
          ) : null}
        </div>

        <button
          type="button"
          className={css.showAllButton}
          onClick={() => openAt(0)}
          aria-label={showAllAriaLabel}
        >
          <GridIcon />
          <span className={css.showAllText}>{showAllLabel}</span>
        </button>
      </div>

      {/* Mobile carousel */}
      <div className={css.mobile}>
        <div className={css.scroller} ref={scrollerRef}>
          {images.map((img, i) => (
            <div className={css.slide} key={img.id?.uuid || i}>
              <button
                type="button"
                className={css.slideButton}
                onClick={() => openAt(i)}
                aria-label={altFor(i)}
              >
                <ResponsiveImage
                  rootClassName={css.slideImage}
                  image={img}
                  alt={altFor(i)}
                  variants={variants}
                  sizes={i === 0 ? HERO_SIZES : '100vw'}
                  {...(i === 0 ? heroImageProps : deferredImageProps)}
                />
              </button>
            </div>
          ))}
        </div>
        <div className={css.counter}>
          <FormattedMessage
            id="SectionPhotoMosaic.photoCounter"
            values={{ current: activeIdx + 1, total: images.length }}
          />
        </div>
        {images.length > 1 ? (
          <div className={css.dots} aria-hidden="true">
            {images.map((img, i) => (
              <span
                key={img.id?.uuid || i}
                className={classNames(css.dot, { [css.dotActive]: i === activeIdx })}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SectionPhotoMosaic;
